/**
 * One campaign: who it goes to, what it says, how fast it goes out, and who it
 * has already reached.
 *
 * The editing model is a draft plus the loaded original, and Save sends only
 * the keys that actually differ. That is not tidiness - `campaignPatchSchema`
 * accepts a partial body precisely so a screen that knows about six fields
 * cannot blank the other twenty by sending them as empty. Computing the diff
 * here is the client half of that guarantee.
 *
 * This file is the shell only: the pinned header, the readiness strip and the
 * tab frame. Everything with a form in it lives in panels/, and the state that
 * decides whether the campaign may run at all lives in detail/readiness.ts.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, CircleAlert, Loader2, Megaphone } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { hostSiteId, userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  getCampaign,
  patchCampaign,
  setCampaignStatus,
  type Campaign,
  type CampaignStatus,
  type SkipReason,
} from "../../../lib/marketing";
import { getMailStats, type MailStats } from "../../../lib/mail";
import { AudiencePanel } from "./panels/AudiencePanel";
import { MessagePanel } from "./panels/MessagePanel";
import { SendingPanel } from "./panels/SendingPanel";
import { RunPanel } from "./panels/RunPanel";
import { RecipientsPanel } from "./panels/RecipientsPanel";
import { RepliesPanel } from "./panels/RepliesPanel";
import { CampaignHeader } from "./detail/CampaignHeader";
import { ReadinessStrip } from "./detail/ReadinessStrip";
import { readiness, type CheckTab } from "./detail/readiness";
import { canon } from "./lib";
import { EmptyState } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { Note, num } from "./shared";

/** Keys the editor may send. Counters, timestamps and the ledger are the
 *  server's, and listing them here rather than diffing the whole object keeps
 *  a stray `sent: 0` out of a PATCH body. */
const EDITABLE = [
  "name", "slug", "description", "locale",
  "subject", "preheader", "bodyHtml", "bodyText", "design", "templateId",
  "fromName", "fromAddress", "replyTo",
  "audience",
  "perRunCap", "dailyCap", "minGapSeconds", "recontactDays",
  "dedupeScope", "skipShared", "sharedAddressThreshold",
  "quietFromHour", "quietToHour", "timezone",
  "autoEnabled",
] as const;

type EditableKey = (typeof EDITABLE)[number];

/** Nested objects that came back through `jsonb`, which does not preserve key
 *  order. Both are compared with `canon` rather than a plain stringify, or the
 *  form sits permanently dirty offering to save an edit nobody made - see lib.ts. */
const JSON_KEYS = new Set<EditableKey>(["audience", "design"]);

/** The changed subset. */
function diff(original: Campaign, draft: Campaign): Partial<Record<EditableKey, unknown>> {
  const out: Partial<Record<EditableKey, unknown>> = {};
  for (const k of EDITABLE) {
    const a = original[k];
    const b = draft[k];
    const same = JSON_KEYS.has(k) ? canon(a) === canon(b) : a === b;
    if (!same) out[k] = b;
  }
  return out;
}

/** The five tabs, in the order the campaign is actually built. */
const TABS = ["audience", "message", "sending", "recipients", "replies"] as const;
type Tab = (typeof TABS)[number];

/**
 * A tab, with an optional marker.
 *
 * The marker is the reason the tabs are not five plain words: an unsubscribe
 * link missing on the message tab is invisible from the audience tab, and
 * finding it should not require opening every tab in turn.
 *
 * `PillTabs` is not used here because its count slot takes a number and these
 * badges are "!" as often as they are a figure - and they carry a severity,
 * which a count does not have.
 */
function TabButton({
  value,
  current,
  label,
  badge,
  tone,
  onSelect,
}: {
  value: Tab;
  current: Tab;
  label: string;
  badge?: string;
  tone?: "warn" | "bad";
  onSelect: (v: Tab) => void;
}) {
  const on = value === current;
  return (
    <button type="button" role="tab" aria-selected={on} className={cn(on && "is-on")} onClick={() => onSelect(value)}>
      {label}
      {badge ? (
        <span
          className="crm-tab-badge crm-tnum"
          style={
            tone === "bad"
              ? {
                  background: "color-mix(in oklab, var(--color-destructive) 16%, transparent)",
                  color: "var(--color-destructive)",
                }
              : tone === "warn"
                ? {
                    background: "color-mix(in oklab, var(--color-warning) 22%, transparent)",
                    color: "color-mix(in oklab, var(--color-warning) 52%, var(--color-foreground))",
                  }
                : undefined
          }
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

export function CampaignDetail({ user }: { user: StaffUser }) {
  const t = useT();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const canManage = userCanPlatform(user, "manage:crm.marketing");

  const [original, setOriginal] = useState<Campaign | null>(null);
  const [draft, setDraft] = useState<Campaign | null>(null);
  const [skipReasons, setSkipReasons] = useState<Partial<Record<SkipReason, number>>>({});
  /** Threads this campaign started, counted server-side. Drives the tab badge
   *  without this screen having to load the inbox to find out. */
  const [replyThreads, setReplyThreads] = useState(0);
  const [mail, setMail] = useState<MailStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<Tab>("audience");

  const load = useCallback(async () => {
    try {
      const res = await getCampaign(id);
      setOriginal(res.campaign);
      setDraft(res.campaign);
      setSkipReasons(res.skipReasons);
      setReplyThreads(res.replyThreads);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Which mailbox is actually polled. Read once: it is a platform setting, not
  // a property of this campaign, and the Replies tab needs it to explain an
  // empty list honestly.
  useEffect(() => {
    getMailStats()
      .then(setMail)
      .catch(() => {
        /* the campaign screen works without it */
      });
  }, []);

  const changes = useMemo(() => (original && draft ? diff(original, draft) : {}), [original, draft]);
  const dirty = Object.keys(changes).length > 0;
  const ready = useMemo(() => (draft ? readiness(draft) : null), [draft]);

  // A half-typed campaign is worth warning about; the browser prompt is crude
  // but it is the only thing that survives a closed tab.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const patch = useCallback((p: Partial<Campaign>) => {
    setDraft((d) => (d ? { ...d, ...p } : d));
  }, []);

  const goTo = useCallback((next: CheckTab) => setTab(next), []);

  async function save() {
    if (!draft || !dirty) return;
    setSaving(true);
    try {
      const res = await patchCampaign(draft.id, changes as Record<string, unknown>);
      setOriginal(res.campaign);
      setDraft(res.campaign);
      setError(null);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409 ? t("marketing.error.slugTaken") : t("marketing.error.save"));
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus() {
    if (!draft) return;
    const next: CampaignStatus = draft.status === "active" ? "paused" : "active";
    try {
      await setCampaignStatus(draft.id, next);
      // Re-read rather than patching state: activating recomputes nothing here,
      // but a refused activation must not leave the pill showing "active".
      await load();
      setError(null);
    } catch (e) {
      const missing = (e as { body?: { missing?: string[] } })?.body?.missing;
      setError(
        missing?.length
          ? t("marketing.error.incomplete", { fields: missing.map((f) => t(`marketing.field.${f}`)).join(", ") })
          : t("marketing.error.status"),
      );
    }
  }

  if (notFound) {
    return (
      <div className="crm-card">
        <EmptyState
          icon={Megaphone}
          title={t("marketing.notFound")}
          body={t("marketing.notFoundBody")}
          action={
            <button type="button" className="crm-btn" onClick={() => navigate("/marketing/campaigns")}>
              <ChevronLeft className="size-4" />
              {t("marketing.back")}
            </button>
          }
        />
      </div>
    );
  }

  if (!draft || !original || !ready) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  const messageOpen = ready.checks.filter((c) => c.tab === "message" && !c.ok);
  const messageBlocking = messageOpen.some((c) => c.severity === "blocking");
  const domainTrap = draft.dedupeScope === "domain" && draft.audience?.source === "register";

  const badges: Record<Tab, { badge?: string; tone?: "warn" | "bad" }> = {
    audience: { badge: draft.audience == null ? "!" : undefined, tone: "bad" },
    message: {
      badge: messageOpen.length ? String(messageOpen.length) : undefined,
      tone: messageBlocking ? "bad" : "warn",
    },
    sending: { badge: domainTrap ? "!" : undefined, tone: "warn" },
    recipients: { badge: draft.queued > 0 ? num(draft.queued) : undefined },
    replies: { badge: replyThreads > 0 ? num(replyThreads) : undefined },
  };

  return (
    // One open menu at a time across the whole editor: the panels below carry
    // row menus of their own.
    <PopHost>
      <div className="pb-10">
        <CampaignHeader
          campaign={draft}
          canManage={canManage}
          dirty={dirty}
          saving={saving}
          blocked={ready.blocking > 0}
          onSave={() => void save()}
          onDiscard={() => setDraft(original)}
          onToggleStatus={() => void toggleStatus()}
        />

        {error ? (
          <Note tone="bad" icon={<CircleAlert className="size-3.5" />} className="mb-3.5">
            {error}
          </Note>
        ) : null}

        <ReadinessStrip state={ready} onGo={goTo} />

        {/* Plain buttons in the app's tab strip rather than Radix tabs: the
            panels below are long forms that own their own focus, and the
            roving-tabindex a tablist brings put the whole form one Tab key
            behind where the operator expected to be. */}
        <div className="crm-tabs mb-4" role="tablist">
          {TABS.map((v) => (
            <TabButton
              key={v}
              value={v}
              current={tab}
              label={t(`marketing.tab.${v}`)}
              badge={badges[v].badge}
              tone={badges[v].tone}
              onSelect={setTab}
            />
          ))}
        </div>

        {tab === "audience" ? <AudiencePanel campaign={draft} onChange={patch} disabled={!canManage} /> : null}
        {tab === "message" ? (
          <MessagePanel campaign={draft} onChange={patch} disabled={!canManage} siteId={hostSiteId(user)} />
        ) : null}
        {tab === "sending" ? (
          <div className="space-y-4">
            <SendingPanel campaign={draft} onChange={patch} disabled={!canManage} />
            {/* The act, under the settings that bound it: an operator reads the
                caps on the way down to the button that obeys them. */}
            <RunPanel campaign={draft} canManage={canManage} />
          </div>
        ) : null}
        {tab === "recipients" ? <RecipientsPanel campaignId={draft.id} skipReasons={skipReasons} canManage={canManage} /> : null}
        {tab === "replies" ? (
          <RepliesPanel
            campaignId={draft.id}
            inboundEnabled={mail?.inboundEnabled ?? false}
            replyMailbox={mail?.inboundAddress ?? ""}
            campaignReplyTo={draft.replyTo || draft.fromAddress}
          />
        ) : null}

        {/* The transport reality, restated where a campaign is actually switched
            on. The list page carries the same warning; a person who deep-links
            straight into a campaign never sees that one. */}
        <Note tone="warn" icon={<AlertTriangle className="size-3.5" />} className="mt-4">
          {t("marketing.transportWarning")}{" "}
          <Link to="/marketing/suppression" className="underline underline-offset-2 hover:text-foreground">
            {t("nav.suppression")}
          </Link>
        </Note>
      </div>
    </PopHost>
  );
}
